import {skipToken} from '@reduxjs/toolkit/query';
import {Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetMetadaServiceCaseQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {workshopRoutes} from '@dms/routes';
import {getCustomerName} from '@dms/shared';

import {buildArray, composePath, parseDate} from 'shared/web';

import {PairedRecordView} from './PairedRecordView';
import {resolvePairedRecordCode} from './resolvePairedRecordCode';

export function useServiceCasePairedRecord(serviceCaseId: string | null): PairedRecordView {
  const {data: serviceCase, isLoading} = useGetMetadaServiceCaseQuery(
    isNotNil(serviceCaseId) ? {serviceCaseId} : skipToken
  );

  const {data: customer} = useGetCustomerV2Query(
    isNotNil(serviceCase?.customerId) ? {customerId: serviceCase.customerId} : skipToken
  );

  const formatDateTime = useDateTimeFormatter();

  const customerName = getCustomerName(customer);
  const createdAtFormatted = serviceCase?.createdAt
    ? formatDateTime('dateShort', parseDate(serviceCase.createdAt))
    : null;
  const closedAtFormatted = serviceCase?.closedAt
    ? formatDateTime('dateShort', parseDate(serviceCase.closedAt))
    : null;

  const parameters = buildArray<Parameter>()
    .when(isNotNil(customerName), `${i18n.t('entity.customer.labels.customer')}: ${customerName}`)
    .when(isNotNil(createdAtFormatted), `${i18n.t('general.labels.created')} ${createdAtFormatted}`)
    .when(isNotNil(closedAtFormatted), `${i18n.t('general.labels.closed')} ${closedAtFormatted}`);

  return {
    isLoading,
    code: resolvePairedRecordCode(serviceCase?.number, isLoading),
    detailPath: isNotNil(serviceCaseId)
      ? composePath(workshopRoutes.serviceCaseDetail, {params: {id: serviceCaseId}})
      : null,
    parameters,
    vehicleIds: isNotNil(serviceCase?.vehicleId) ? [serviceCase.vehicleId] : [],
  };
}
